/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jan 30 2024
 **/

import { useTheme } from "@emotion/react";
import { Column, Icon, Row, Typography } from "@/frameworks/designStuff/components";
import { Avatar } from "@/frameworks/core/components";
import { ChronoUtils } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";

import { AvatarWrap, ToolButtonWrap, ViewerHeaderWrap } from "./emotion";
import InputRange from "./inputRange/InputRange";
import { ViewerHeaderProps } from "./HeaderWrapper";

export default function Header({
	createByID,
	fileName,
	publisherName,
	date,
	fileDisplaySize,
	minimize = false,
	onClickClose,
	onClickDownload,
	onClickDelete,
	onClickMinimize
}: ViewerHeaderProps) {
	const theme = useTheme();

	return (
		<ViewerHeaderWrap verticalAlign="center" horizonAlign="distribute">
			<Row verticalAlign="center">
				<AvatarWrap verticalAlign="center" horizonAlign="center">
					<Avatar kind={"people_28"} id={createByID} />
				</AvatarWrap>
				<Column verticalAlign="center" css={{ maxWidth: "100%" }}>
					<Typography.div
						typoSize="Body2"
						typoColor="neutral_00_100"
						ellipsis
						maxLine={1}
						css={{ width: "100%" }}
						align="left"
					>
						{fileName}
					</Typography.div>
					<Typography.div
						typoSize="Body4"
						typoColor="neutral_00_100"
						ellipsis
						maxLine={1}
						css={{ width: "100%" }}
						align="left"
					>
						{_T("공유자: {{publisherName}}", { publisherName: publisherName })}
						{date ? ` | ${ChronoUtils.formatPolicyDateOnly(date)}` : ""}{" "}
						{fileDisplaySize ? ` | ${fileDisplaySize}` : ""}
					</Typography.div>
				</Column>
			</Row>
			<Row
				gap={16}
				horizonAlign="center"
				verticalAlign="center"
				css={{ width: "100%", justifyContent: "end", padding: "0 16px" }}
			>
				<Row
					css={{
						justifyContent: "center",
						alignItems: "center",
						display: minimize ? "none" : "flex"
					}}
				>
					<InputRange minimize={minimize} />
				</Row>
				{onClickMinimize && (
					<ToolButtonWrap onClick={onClickMinimize}>
						<Icon
							name={minimize ? "Maximize24" : "Closemaxi24"}
							size="24"
							fill={theme.colors["neutral_00_100"]}
						/>
					</ToolButtonWrap>
				)}
				{onClickDelete && (
					<ToolButtonWrap onClick={onClickDelete}>
						<Icon name="Delete24" size="24" fill={theme.colors["neutral_00_100"]} />
					</ToolButtonWrap>
				)}
				{onClickDownload && (
					<ToolButtonWrap onClick={onClickDownload}>
						<Icon name="Download24" size="24" fill={theme.colors["neutral_00_100"]} />
					</ToolButtonWrap>
				)}
				{onClickClose && (
					<ToolButtonWrap onClick={onClickClose}>
						<Icon name="Close24" size="24" fill={theme.colors["neutral_00_100"]} />
					</ToolButtonWrap>
				)}
			</Row>
		</ViewerHeaderWrap>
	);
}
